<script setup>
import { useI18n } from 'vue-i18n';
import MaterialIcon from '@/components/common/MaterialIcon.vue';

const { t } = useI18n();
const open = defineModel({ type: Boolean, default: false });
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
        role="dialog"
        aria-modal="true"
        :aria-label="t('contact.title')"
      >
        <div
          class="modal-backdrop absolute inset-0 bg-slate-900/45"
          @click="open = false"
        />

        <div class="modal-panel relative z-10 w-full max-w-[26rem]">
          <div class="relative overflow-hidden rounded-xl border border-border bg-card shadow-xl shadow-slate-900/10">
            <button
              type="button"
              class="absolute right-3 top-3 grid size-8 place-items-center rounded-md text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground"
              :aria-label="t('contact.close')"
              @click="open = false"
            >
              <MaterialIcon name="close" class="text-xl" />
            </button>

            <div class="px-6 pb-7 pt-7 sm:px-8 sm:pb-8 sm:pt-8">
              <p class="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                {{ t('contact.title') }}
              </p>
              <h2 class="mt-2 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                {{ t('contact.heading') }}
              </h2>
              <p class="mt-2 text-sm leading-relaxed text-muted-foreground">
                {{ t('contact.desc') }}
              </p>

              <div class="mt-7 flex justify-center">
                <div class="rounded-lg border border-border bg-muted/40 p-3">
                  <img
                    src="/images/ewm.png"
                    :alt="t('contact.qrAlt')"
                    class="size-44 object-contain sm:size-48"
                    width="192"
                    height="192"
                    loading="lazy"
                  />
                </div>
              </div>

              <p class="mt-5 text-center text-xs leading-relaxed text-muted-foreground">
                {{ t('contact.qrHint') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.28s ease;
}

.modal-enter-active .modal-panel,
.modal-leave-active .modal-panel {
  transition:
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.28s ease;
}

.modal-enter-active .modal-backdrop,
.modal-leave-active .modal-backdrop {
  transition: opacity 0.28s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-panel,
.modal-leave-to .modal-panel {
  opacity: 0;
  transform: scale(0.94) translateY(12px);
}

.modal-enter-from .modal-backdrop,
.modal-leave-to .modal-backdrop {
  opacity: 0;
}
</style>
